import { AnimatePresence, motion } from 'motion/react';
import { useContext } from 'react';

import cx from 'classnames';

import { Button, Icon, Text } from 'components/@tedui';
import { NewsletterForm } from 'components/NewsletterForm';
import { Link } from 'components/router';

import { useSettingsDashboardQuery } from 'api';

import { MIXPANEL_PRODUCT } from 'lib/analytics/constants';
import mixpanel from 'lib/analytics/mixpanel';
import { getSystemLanguage } from 'lib/analytics/mixpanel-helpers';
import { useAuthenticated } from 'lib/auth';
import { useSailthru } from 'lib/hooks/useSailthru';

import useVideoPlayerStore from 'components/video-player/store';
import { NewslettersContext } from 'pages/newsletters';

const Toast = () => {
  const {
    showOverlay,
    closeOverlay,
    newsletterName,
    newsletterFrequencyOption,
    subscribed,
    setSubscribed
  } = useContext(NewslettersContext);
  const autoplayed = useVideoPlayerStore(store => store.autoplayed);
  const isLoggedIn = useAuthenticated();
  const { loading, data } = useSettingsDashboardQuery({
    ssr: true,
    skip: !isLoggedIn
  });
  const user = !loading && data?.viewer;

  const sailthru = useSailthru();

  const handleSubscribe = email => {
    let formName;

    if (newsletterName === 'TED Ed') {
      if (newsletterFrequencyOption !== 'Weekly') {
        formName = 'TED Ed Daily';
      } else {
        formName = 'TED Ed Weekly';
      }
    } else {
      formName = newsletterName;
    }

    mixpanel.track('form_submit', {
      product: MIXPANEL_PRODUCT,
      form_name: `Newsletter Gallery ${formName}`,
      system_language: getSystemLanguage(),
      autoplay_state: autoplayed
    });

    sailthru.signUp({
      email,
      newsletterName,
      frequency: newsletterFrequencyOption,
      onSuccess() {
        setSubscribed(true);
        setTimeout(() => {
          closeOverlay();
        }, 5000);
      },
      onError() {
        alert('We encountered an issue signing you up. Please try again');
      }
    });
  };

  const colors = {
    'TED Talks Daily': '#B2BFFB',
    'TED Recommends': '#FEEC8E',
    'TED Ed': '#FFCEC2'
  };

  const wrapperClasses = cx(`sticky left-0 right-0 z-50 px-10`, {
    'bottom-9 mx-auto w-11/12 rounded-md': subscribed,
    'bottom-0 flex w-full flex-col rounded-t-md': !subscribed
  });

  return (
    <AnimatePresence>
      {showOverlay && (
        <motion.div
          style={{ backgroundColor: colors[newsletterName] }}
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ delay: 0.4 }}
          className={wrapperClasses}
        >
          {subscribed ? (
            <div className="flex items-center py-4">
              <div className="flex flex-1 flex-col items-center justify-end">
                <Text variant="header4" isBold>
                  <span>You&apos;re subscribed</span>
                  <span className="hidden md-tui:inline">{` to ${newsletterName}`}</span>
                  {newsletterFrequencyOption && (
                    <span>{` ${newsletterFrequencyOption}`}</span>
                  )}
                  <span>!</span>
                </Text>
              </div>
              <button
                type="button"
                aria-label="Dismiss notice"
                onClick={closeOverlay}
                className="hover:opacity-50"
              >
                <Icon iconName="x" className="text-2xl" />
              </button>
            </div>
          ) : (
            <>
              <div className="flex justify-end pb-2 pt-4">
                <button
                  type="button"
                  aria-label="Dismiss notice"
                  onClick={closeOverlay}
                  className="hover:opacity-50"
                >
                  <Icon iconName="x" className="text-2xl" />
                </button>
              </div>
              <div className="mx-auto md-tui:w-1/3">
                {isLoggedIn ? (
                  <div>
                    <div className="mb-2 text-center">
                      <Text variant="subheader2">
                        {user.primaryEmailAddress}
                      </Text>
                    </div>
                    <Button
                      UNSAFE_className="flex justify-center rounded-r-sm align-middle bg-white border-solid border-thin mx-auto mb-3"
                      text="Subscribe"
                      isFullWidth={false}
                      testId="Footer_Newsletter_Subscribe"
                      variant="tertiary"
                      onClick={() => handleSubscribe(user.primaryEmailAddress)}
                    />
                  </div>
                ) : (
                  <NewsletterForm onSubscribe={handleSubscribe} />
                )}
                <Text tag="p" variant="caption" UNSAFE_className="my-1 mb-9">
                  By subscribing, you understand and agree that we will store,
                  process and manage your personal information according to our{' '}
                  <Link
                    href="/about/our-organization/our-policies-terms/privacy-policy"
                    className="underline"
                  >
                    Privacy Policy
                  </Link>
                  .
                </Text>
              </div>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Toast;
